import { TabledRepeat } from "lib/@components/tabled-repeat/tabled-repeat"
import { AddGroupScheduleRow } from "./add-group-schedule-row"
import { Checkbox, Stack, Typography } from "@mui/material"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useEffect, useMemo, useState } from "react"
import { ResetTabledRepeat } from "event-definitions"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { omit } from "lib/omit"
import { useDialog } from "lib/@hooks/useDialog"
import { AuthoringDeleteDialog } from "../../plugins/project-item/authoring-delete-dialog"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { deleteSchedulesFromAddGroup } from "./controller/delete-schedules-from-add-group"
import { useParams } from "react-router"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { ContextMenu } from "lib/@components/context-menu-bar"
import { replaceAllWith } from "lib/@components/slot/replace-all-with"
import { DeleteMenuItem } from "../../plugins/delete-menu-item"
import { getDraftOrPublishedSchedules } from "authoring/routes/project/controller/get-draft-or-published-schedules"
import { refreshOnlyFor } from "lib/graphql/api"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { SelectedItemsBanner } from "../../components/selected-items-banner"
import { If } from "lib/@components/switch"
import { GroupStatus } from "authoring/lib/enum/group-status.mjs"
import { isLockedToLegacy } from "authoring/lib/enum/project-phase.mjs"
import { compareByCode } from "authoring/lib/compare-by-code"
import { useDemand } from "lib/authorization/use-demand"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function AddGroupSchedules() {
    const { projectId, groupId } = useParams()
    const { project, projectGroup, groupLoading } = useBoundContext()
    const canEditSchedule = useDemand(AUTHORING_EDIT_SCHEDULES_DEMAND)
    const [selectedSchedules, setSelectedSchedules] = useState({})
    const selectedScheduleIds = Object.keys(selectedSchedules)
    const scheduleIds = (projectGroup?.addTypeSchedules ?? []).map("id")
    const schedulesResult = getDraftOrPublishedSchedules.useResults(
        { projectId, scheduleIds },
        refreshOnlyFor(projectId),
        withInitialValue(IS_LOADING)
    )
    const schedulesLoading = schedulesResult === IS_LOADING
    const schedules = schedulesLoading ? undefined : schedulesResult
    const [searchParam] = useSearchParam("search", "")
    const search = searchParam.toLowerCase()
    const scheduleList = schedules ?? []
    const filteredSchedules = useMemo(
        () => scheduleList.filter(matchesSearch).sort(compareByCode),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [scheduleList, search]
    )
    const scheduleString = selectedScheduleIds?.length > 1 ? "schedules" : "schedule"
    const allSelected = selectedScheduleIds?.length === filteredSchedules?.length
    const someSelected = selectedScheduleIds.length > 0 && !allSelected
    const checkboxHeader = canEditSchedule ? (
        <Checkbox
            indeterminate={someSelected}
            checked={allSelected}
            onChange={handleToggleAll}
            disabled={projectGroup.readOnly}
        />
    ) : null
    const disableDelete = projectGroup.status === GroupStatus.APPLIED || isLockedToLegacy(project?.phase)
    const tableHeaders = [
        {
            title: checkboxHeader,
        },
        { title: "Name" },
        { title: "Code" },
        { title: "Tasks", align: "center" },
        { title: "" },
        { title: "" },
    ]

    const deleteSchedulesDialog = useDialog(
        <AuthoringDeleteDialog
            value={true}
            title={`Delete ${selectedScheduleIds.length > 1 ? selectedScheduleIds.length : ""} ${scheduleString.titleize()}`}
            message={`This action cannot be undone, but the ${scheduleString} can be re-added manually`}
        />
    )
    const deleteMenuItem = canEditSchedule ? (
        <DeleteMenuItem handleDelete={handleDelete} disabled={projectGroup.readOnly} />
    ) : null
    ContextMenu("addTasks").plug(replaceAllWith(deleteMenuItem))

    useEffect(() => {
        ResetTabledRepeat.raiseOnce()
    }, [searchParam, selectedSchedules, schedulesResult])

    useEffect(() => {
        if (!canEditSchedule && Object.keys(selectedSchedules).length) setSelectedSchedules({})
    }, [canEditSchedule, selectedSchedules])

    if (groupLoading || schedulesLoading) return <LoadingScreen />

    return (
        <Stack spacing={2}>
            <Typography sx={{ fontWeight: "medium" }}>Schedules</Typography>
            <SearchBoxUsingSearchParam />
            <If value={canEditSchedule}>
                <SelectedItemsBanner
                    selected={selectedScheduleIds}
                    label={"schedule"}
                    handleDelete={handleDelete}
                    disabled={disableDelete}
                />
            </If>
            <TabledRepeat
                tableHeaders={tableHeaders}
                itemPass="schedule"
                items={filteredSchedules}
                itemComponent={<AddGroupScheduleRow selected={selectedSchedules} handleSelect={handleSelect} />}
                alternating={false}
                paged={true}
                headerStyles={{ backgroundColor: "background.paper" }}
                sx={{ "& tr td.MuiTableCell-root:last-of-type": { px: 0 } }}
            />
        </Stack>
    )

    function matchesSearch(schedule) {
        const title = schedule?.title ?? ""
        const code = schedule?.code ?? ""
        return title.toLowerCase().includes(search) || code.toLowerCase().includes(search)
    }

    function handleToggleAll() {
        if (allSelected || someSelected) return setSelectedSchedules({})
        const newSelection = {}
        for (const { id } of filteredSchedules) newSelection[id] = true
        return setSelectedSchedules(newSelection)
    }

    function handleSelect(scheduleId) {
        const deselect = Boolean(selectedSchedules[scheduleId])
        if (deselect) {
            setSelectedSchedules((selected) => omit(selected, scheduleId))
        } else {
            setSelectedSchedules((selected) => ({ ...selected, [scheduleId]: true }))
        }
    }

    async function handleDelete(scheduleIds) {
        try {
            const confirm = await deleteSchedulesDialog()
            if (!confirm) return

            const deleted = await deleteSchedulesFromAddGroup({
                projectId,
                groupId,
                scheduleIds,
            })
            if (!deleted) {
                errorSnackbar(`Failed to delete ${scheduleString} from group`)
                return
            }

            successSnackbar(`Successfully deleted ${scheduleString}`)
        } catch (error) {
            console.error(error)
            errorSnackbar(`Unexpected error while deleting ${scheduleString}`)
        }
    }
}
